import type { MfaSetupDto, MfaStatusDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Code, CopyButton, Divider, Group, Image, Modal, PasswordInput, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { formatDateTime } from '../format';
import { notifyOk } from '../notify';
import { QueryState } from './QueryState';
import { IconAlertTriangle, IconDownload, IconShieldCheck, IconShieldOff } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorText } from '../api';
import { useMe } from '../auth';
import { ask } from '../confirm';
import { datePl } from '@bqcrm/shared';
import { ChangePasswordSection } from './ChangePassword';
import { MobileDevices } from './MobileDevices';
import { PasskeysSection } from './Passkeys';

/** Kod gotowy do wysłania: 6 cyfr z aplikacji albo kod zapasowy „XXXX-XXXX” (wielkość liter, myślnik i spacje bez znaczenia — serwer normalizuje). */
export const mfaCodeOk = (code: string, recovery: boolean) => (recovery ? code.replace(/[\s-]/g, '').length >= 8 : /^\d{6}$/.test(code.trim()));

/** Plik tekstowy z kodami zapasowymi (do zapisania na dysku / wydruku). */
function downloadCodes(codes: string[], email: string | undefined) {
  const text = ['Eko CRM — kody zapasowe logowania dwuetapowego', `Konto: ${email ?? ''}`, `Wygenerowano: ${formatDateTime(new Date().toISOString())}`, '', 'Każdy kod działa jeden raz, gdy nie masz przy sobie telefonu z aplikacją uwierzytelniającą.', '', ...codes, ''].join('\r\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'eko-crm-kody-zapasowe.txt';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Bezpieczeństwo logowania własnego konta: zmiana hasła, klucze dostępu i logowanie dwuetapowe (aplikacja uwierzytelniająca). */
export function MfaSettings({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const me = useMe();
  const status = useQuery({
    queryKey: ['mfa'],
    queryFn: () => api<MfaStatusDto>('/auth/mfa'),
    enabled: opened,
  });
  const [setup, setSetup] = useState<MfaSetupDto | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSetup(null);
    setCode('');
    setRecovery(false);
    setPassword('');
    setCodes(null);
    setError(null);
  };
  const close = () => {
    reset();
    onClose();
  };
  /** Zamknięcie krzyżykiem, gdy kody zapasowe są na ekranie — tylko po potwierdzeniu (nie pokażemy ich ponownie). */
  const requestClose = async () => {
    if (codes && !(await ask('Kody zapasowe nie zostaną pokazane ponownie. Zapisane?', { title: 'Zamknąć okno?', confirm: 'Tak, zapisane' }))) return;
    close();
  };
  const fail = (e: unknown) => setError(errorText(e));

  const start = useMutation({
    mutationFn: () => api<MfaSetupDto>('/auth/mfa/setup', { method: 'POST' }),
    onSuccess: (d) => (setError(null), setSetup(d)),
    onError: fail,
  });
  const enable = useMutation({
    mutationFn: () =>
      api<{ recoveryCodes: string[] }>('/auth/mfa/enable', {
        method: 'POST',
        body: { code },
      }),
    onSuccess: (d) => {
      setError(null);
      setSetup(null);
      setCodes(d.recoveryCodes);
      void qc.invalidateQueries({ queryKey: ['mfa'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: fail,
  });
  const disable = useMutation({
    mutationFn: () => api('/auth/mfa/disable', { method: 'POST', body: { password, code } }),
    onSuccess: () => {
      reset();
      void qc.invalidateQueries({ queryKey: ['mfa'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      notifyOk('Logowanie dwuetapowe wyłączone');
    },
    onError: fail,
  });

  const s = status.data;
  return (
    <Modal opened={opened} onClose={() => void requestClose()} closeOnClickOutside={!codes} closeOnEscape={!codes} title="Hasło i bezpieczeństwo logowania" size="lg">
      <Stack gap="lg">
        <ChangePasswordSection />
        <Divider />
        {me.data && (
          <Stack gap="sm">
            <div>
              <Text fw={700}>Telefony z aplikacją mobilną</Text>
              <Text size="sm" c="dimmed">
                Aktywuj aplikację mobilną na swoim telefonie kodem QR.
              </Text>
            </div>
            <MobileDevices userId={me.data.id} canRevoke={false} />
          </Stack>
        )}
        <Divider />
        <PasskeysSection enabled={opened} />
        <Divider />
        <Stack gap="sm">
          <div>
            <Text fw={700}>Logowanie dwuetapowe</Text>
            <Text size="sm" c="dimmed">
              Przy logowaniu hasłem — dodatkowo 6-cyfrowy kod z aplikacji uwierzytelniającej. Logowanie kluczem dostępu nie wymaga kodu.
            </Text>
          </div>
          {codes ? (
            <Stack>
              <Alert color="green" icon={<IconShieldCheck />} title="Włączone">
                Od teraz przy logowaniu, po haśle, aplikacja poprosi o kod z telefonu.
              </Alert>
              <Text size="sm">
                <b>Kody zapasowe</b> — zapisz je w bezpiecznym miejscu. Każdy działa raz, gdy nie masz przy sobie telefonu. Nie pokażemy ich ponownie.
              </Text>
              <SimpleGrid cols={2} spacing="xs">
                {codes.map((c) => (
                  <Code key={c} fz="md" ta="center" py={6}>
                    {c}
                  </Code>
                ))}
              </SimpleGrid>
              <Group justify="flex-end">
                <Button variant="default" leftSection={<IconDownload size={16} />} onClick={() => downloadCodes(codes, me.data?.email)}>
                  Pobierz plik .txt
                </Button>
                <CopyButton value={codes.join('\n')}>
                  {({ copied, copy }) => (
                    <Button variant="default" onClick={copy}>
                      {copied ? 'Skopiowano' : 'Kopiuj kody'}
                    </Button>
                  )}
                </CopyButton>
                <Button onClick={close}>Kody zapisane</Button>
              </Group>
            </Stack>
          ) : status.isError ? (
            <Alert color="red" icon={<IconAlertTriangle />} title="Nie udało się pobrać stanu logowania dwuetapowego">
              <Stack gap="xs" align="flex-start">
                <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                  {errorText(status.error)}
                </Text>
                <Button size="xs" variant="light" color="red" loading={status.isFetching} onClick={() => void status.refetch()}>
                  Ponów
                </Button>
              </Stack>
            </Alert>
          ) : !s ? (
            <QueryState isLoading error={null} lines={2}>{null}</QueryState>
          ) : s.enabled ? (
            <Stack>
              <Group gap="xs">
                <Badge color="green" leftSection={<IconShieldCheck size={14} />}>
                  Włączone
                </Badge>
                <Text size="sm" c="dimmed">
                  od {datePl(s.enabledAt)} · kodów zapasowych: {s.recoveryLeft}
                </Text>
              </Group>
              <Text size="sm">Aby wyłączyć, podaj hasło i aktualny kod z aplikacji (albo kod zapasowy).</Text>
              {error && <Alert color="red">{error}</Alert>}
              <PasswordInput label="Hasło" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
              <Stack gap={4}>
                <TextInput
                  key={recovery ? 'zapasowy' : 'totp'}
                  label={recovery ? 'Kod zapasowy' : 'Kod z aplikacji'}
                  placeholder={recovery ? 'ABCD-EFGH' : '123456'}
                  autoComplete={recovery ? 'off' : 'one-time-code'}
                  inputMode={recovery ? undefined : 'numeric'}
                  autoCapitalize={recovery ? 'characters' : undefined}
                  spellCheck={false}
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value)}
                  maxLength={recovery ? 11 : 6}
                />
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  ta="left"
                  onClick={() => {
                    setRecovery(!recovery);
                    setCode('');
                  }}
                >
                  {recovery ? 'Mam kod z aplikacji' : 'Mam kod zapasowy'}
                </Anchor>
              </Stack>
              <Group justify="flex-end">
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconShieldOff size={16} />}
                  loading={disable.isPending}
                  disabled={!password || !mfaCodeOk(code, recovery)}
                  onClick={() => disable.mutate()}
                >
                  Wyłącz logowanie dwuetapowe
                </Button>
              </Group>
            </Stack>
          ) : setup ? (
            <Stack>
              <Text size="sm">1. Otwórz aplikację uwierzytelniającą (Google Authenticator, Microsoft Authenticator, Authy…) i zeskanuj kod.</Text>
              <Group align="flex-start" wrap="wrap">
                <Image src={setup.qr} w={200} h={200} alt="Kod QR do aplikacji uwierzytelniającej" style={{ background: '#fff', borderRadius: 8 }} />
                <Stack gap={4} style={{ flex: 1, minWidth: 200 }}>
                  <Text size="xs" c="dimmed">
                    Nie możesz zeskanować? Wpisz klucz ręcznie:
                  </Text>
                  <Code fz="sm" style={{ wordBreak: 'break-all' }}>
                    {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
                  </Code>
                </Stack>
              </Group>
              <Text size="sm">2. Wpisz 6-cyfrowy kod, który pokazuje aplikacja.</Text>
              {error && <Alert color="red">{error}</Alert>}
              <TextInput
                placeholder="123456"
                autoComplete="one-time-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                maxLength={6}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && code.trim().length === 6 && enable.mutate()}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={reset}>
                  Anuluj
                </Button>
                <Button loading={enable.isPending} disabled={code.trim().length !== 6} onClick={() => enable.mutate()}>
                  Włącz
                </Button>
              </Group>
            </Stack>
          ) : (
            <Stack>
              <Text size="sm">
                Logowanie dwuetapowe chroni konto, nawet gdy ktoś pozna Twoje hasło: po haśle trzeba jeszcze wpisać kod z aplikacji w telefonie. Działa też w aplikacji mobilnej.
              </Text>
              {error && <Alert color="red">{error}</Alert>}
              <Group justify="flex-end">
                <Button leftSection={<IconShieldCheck size={16} />} loading={start.isPending} onClick={() => start.mutate()}>
                  Skonfiguruj
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
